import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Sparkles, Users, Award } from 'lucide-react';

const SobrePage = () => {
  return (
    <div className="min-h-screen pt-24 pb-16 px-6">
      <div className="max-w-[1400px] mx-auto space-y-24">

        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-text-primary mb-4">
            Sobre a Aura Rosa
          </h1>
          <p className="text-lg text-text-secondary">
            Celebrando a beleza única de cada mulher
          </p>
        </motion.div>

        {/* NOSSA HISTÓRIA */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <img
              src="https://images.unsplash.com/photo-1737188550464-10ad3e06b466"
              alt="Nossa História"
              className="rounded-2xl w-full h-[600px] object-cover"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h2 className="font-heading text-4xl font-bold text-text-primary">
              Nossa História
            </h2>

            <p className="text-lg text-text-secondary leading-relaxed">
              A Aura Rosa nasceu de uma paixão profunda por realçar a beleza natural e
              única de cada mulher. Acreditamos que cada pessoa possui uma essência
              especial, uma aura que merece ser celebrada e valorizada.
            </p>

            <p className="text-lg text-text-secondary leading-relaxed">
              Nossa jornada começou com uma visão simples: criar um espaço onde mulheres
              pudessem encontrar produtos de beleza e acessórios que não apenas
              embelezam externamente, mas também fortalecem a confiança e a autoestima.
            </p>

            <p className="text-lg text-text-secondary leading-relaxed">
              Cada produto em nossa coleção é cuidadosamente selecionado pensando na
              qualidade, elegância e na experiência que proporcionará. Do momento em
              que você navega em nosso site até receber seu pedido em casa, queremos
              que se sinta especial e valorizada.
            </p>

            <p className="text-lg text-text-secondary leading-relaxed">
              Mais do que uma loja, Aura Rosa é uma celebração da feminilidade em todas
              as suas formas. É sobre se sentir bem consigo mesma, todos os dias.
            </p>
          </motion.div>
        </div>

        {/* NOSSOS VALORES */}
        <section className="bg-white rounded-2xl py-16 px-8 shadow-sm">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="font-heading text-4xl font-bold text-center mb-12 text-text-primary"
          >
            Nossos Valores
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            {[
              {
                icon: <Heart className="text-accent w-7 h-7" />,
                title: 'Autenticidade',
                desc: 'Valorizamos a verdadeira essência de cada mulher'
              },
              {
                icon: <Sparkles className="text-accent w-7 h-7" />,
                title: 'Qualidade',
                desc: 'Produtos cuidadosamente selecionados para você'
              },
              {
                icon: <Users className="text-accent w-7 h-7" />,
                title: 'Comunidade',
                desc: 'Construindo uma rede de mulheres empoderadas'
              },
              {
                icon: <Award className="text-accent w-7 h-7" />,
                title: 'Excelência',
                desc: 'Comprometidos com uma experiência excepcional'
              }
            ].map((item, idx) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -8 }}
                className="group"
              >
                <div className="w-16 h-16 mx-auto mb-5 bg-accent/10 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-105">
                  {item.icon}
                </div>
                <h3 className="font-heading text-2xl font-bold text-text-primary mb-3">
                  {item.title}
                </h3>
                <p className="text-text-secondary leading-relaxed">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* NOSSA MISSÃO */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h2 className="font-heading text-4xl font-bold text-text-primary">
              Nossa Missão
            </h2>

            <p className="text-lg text-text-secondary leading-relaxed">
              Proporcionar uma experiência de compra elegante e inspiradora, oferecendo
              produtos de beleza e acessórios que realçam a confiança e expressam a
              individualidade de cada mulher.
            </p>

            <p className="text-lg text-text-secondary leading-relaxed">
              Queremos ser mais do que uma loja: somos parceiras na jornada de
              autoconhecimento e autocuidado de cada cliente.
            </p>

            <div className="bg-accent/10 p-6 rounded-xl border-l-4 border-accent">
              <p className="italic text-text-secondary text-lg leading-relaxed">
                "Cada mulher possui uma aura única. Nossa missão é ajudá-la a descobrir e
                celebrar essa luz interior através da beleza e do autocuidado."
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <img
            src="https://images.unsplash.com/photo-1585409351049-f0d83b76c5dc"
            alt="Nossa Missão"
            className="rounded-2xl w-full h-[500px] object-cover"
        />
          </motion.div>
        </div>

        {/* NOSSO COMPROMISSO */}
        <section className="bg-accent/10 rounded-2xl py-16 px-8">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto mb-12"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-text-primary mb-6">
              Nosso Compromisso
            </h2>
            <p className="text-xl text-text-secondary leading-relaxed">
              Estamos comprometidos em oferecer não apenas produtos, mas uma experiência
              completa que nutre a confiança, celebra a diversidade e promove o bem-estar
              de todas as nossas clientes.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              {
                title: 'Qualidade Garantida',
                desc: 'Todos os produtos são testados e aprovados'
              },
              {
                title: 'Atendimento Personalizado',
                desc: 'Suporte dedicado para todas as suas dúvidas'
              },
              {
                title: 'Entrega Segura',
                desc: 'Embalagem cuidadosa e rastreamento completo'
              }
            ].map((item, idx) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -8 }}
                className="bg-white p-6 rounded-xl shadow-sm transition-all duration-300 hover:shadow-md"
              >
                <h3 className="font-heading text-2xl font-bold text-accent mb-4">
                  {item.title}
                </h3>
                <p className="text-text-secondary leading-relaxed">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

      </div>
    </div>
  );
};

export default SobrePage;